/**
 * The practice header DATE: today unless the coach picked another one, and
 * never empty (client 2026-10).
 */

/**
 * Today as yyyy-mm-dd in the device's LOCAL time zone. Several apps used
 * `new Date().toISOString().split("T")[0]`, which is the UTC date — in the US
 * evening that is already TOMORROW.
 */
export const todayLocalISO = (now: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Who set the current header date, and to which day.
 * - `user`: the coach picked `day` in the field. It stays — across reloads
 *   and later days — until they pick another date or clear the field, or the
 *   game is done with (Save Game, Clear Playlist, sign-out: see
 *   forgetHeaderDatePick). QB is the exception: its Save Game and Clear
 *   Playlist keep all its header selections, the date included.
 * - `auto`: `day` was filled in automatically; it follows today.
 * null when unknown (first run with this build, after any of those).
 */
export type HeaderDateOrigin = { by: "user" | "auto"; day: string };

const ORIGIN_RE = /^(user|auto):(\d{4}-\d{2}-\d{2})$/;

export const parseHeaderDateOrigin = (raw: string | null | undefined): HeaderDateOrigin | null => {
  const match = ORIGIN_RE.exec(String(raw ?? ""));
  return match ? { by: match[1] as "user" | "auto", day: match[2] as string } : null;
};

export const formatHeaderDateOrigin = (origin: HeaderDateOrigin): string =>
  `${origin.by}:${origin.day}`;

/**
 * The origin to record when the coach picks `day` in the field. Picking
 * TODAY — or clearing the field — is going back to the default, not a pick
 * that should stick: it must keep rolling to the next day like any automatic
 * date.
 */
export const originForPick = (day: string, today: string): HeaderDateOrigin =>
  !day.trim() || day === today ? { by: "auto", day: today } : { by: "user", day };

/**
 * What the header date should be right now.
 *
 * A coach's pick (`user`) wins over anything code puts in the field, empty
 * included:
 * - the field is empty after a reload (QB and ST keep no selections on the
 *   device) → the pick comes back;
 * - RE-RUN copies the PREVIOUS play's date — or no date, before any play was
 *   saved — into the selections → the pick comes back.
 * Dropping a pick is always explicit, never read from the field going empty:
 * the field's own clear records an automatic origin (originForPick), and
 * Save Game, Clear Playlist and sign-out forget it (forgetHeaderDatePick).
 *
 * Anything else (automatic, unknown) → today, so an automatic date from
 * yesterday never quietly dates today's plays.
 */
export const resolveHeaderDate = (
  value: string | null | undefined,
  origin: HeaderDateOrigin | null,
  today: string
): { value: string; origin: HeaderDateOrigin } => {
  const current = (value ?? "").trim();
  if (origin?.by === "user") {
    return current === origin.day ? { value: current, origin } : { value: origin.day, origin };
  }
  return { value: today, origin: { by: "auto", day: today } };
};

/* ── Where the origin is kept ────────────────────────────────────────── */

const ORIGIN_KEY_PREFIX = "practiceDateOrigin:";

// Per app: in the dashboard every app lives under its own first path segment
// (/wr, /qb …), on every page — so the drawer's Save Game on Play List finds
// the same pick as the field on Play Entry. Kept in memory as well, so a pick
// still sticks for this session when device storage is full or blocked (it
// has filled up before).
const memoryOrigins = new Map<string, string>();
const originKey = (scope?: string): string => {
  const app =
    scope ??
    (typeof window === "undefined" ? "app" : window.location.pathname.split("/")[1] || "app");
  return `${ORIGIN_KEY_PREFIX}${app}`;
};

/** This app's header date origin, or null when unknown. */
export const readHeaderDateOrigin = (): HeaderDateOrigin | null => {
  const key = originKey();
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(key);
  } catch {
    /* storage blocked — fall back to memory */
  }
  return parseHeaderDateOrigin(memoryOrigins.get(key) ?? stored);
};

export const writeHeaderDateOrigin = (origin: HeaderDateOrigin): void => {
  const key = originKey();
  const raw = formatHeaderDateOrigin(origin);
  memoryOrigins.set(key, raw);
  try {
    localStorage.setItem(key, raw);
  } catch {
    // Storage full or blocked — the in-memory copy still applies. Drop the
    // stored one, so an older pick can't come back after a reload.
    try {
      localStorage.removeItem(key);
    } catch {
      /* blocked altogether — nothing was kept there */
    }
  }
};

/**
 * The current game is done with (Save Game, Clear Playlist): drop the app's
 * pick, so the next game starts on today — from whichever page it happened.
 * Call it with the reset of the selections (the field re-checks when its
 * value changes, and then goes to today).
 *
 * `app` is the app's id = its path segment ("wr"). Pass it: Save Game ends
 * after network calls, and the coach may have gone Back to the dashboard (or
 * into another app) by then, so the URL no longer says whose pick it is.
 */
export const forgetHeaderDatePick = (app?: string): void => {
  const key = originKey(app);
  memoryOrigins.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage blocked — the memory copy is gone already */
  }
};

/** Sign-out: no app's pick carries over to whoever signs in next. */
export const forgetAllHeaderDatePicks = (): void => {
  memoryOrigins.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(ORIGIN_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage blocked — nothing was kept there */
  }
};

/* ── Game dates (Game Library) ─────────────────────────────────────────
 * `game_date` is a UTC timestamp — the server stamps the moment the game was
 * saved — so the hour is kept. Lists show only the local DAY (in-game parity:
 * lib/localDate in platform-2.0). A bare `YYYY-MM-DD` (older cached data) is a
 * calendar day and must NOT go through `new Date("2026-09-18")`, which is
 * midnight UTC = the previous evening in the US.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A game date as a local Date: a timestamp as-is, a bare day as local midnight. */
export const toLocalGameDate = (value?: string | null): Date | null => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const match = DATE_ONLY.exec(trimmed);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** The local calendar day of a game date (`YYYY-MM-DD`), or "". */
export const formatLocalGameDate = (value?: string | null): string => {
  const date = toLocalGameDate(value);
  return date ? todayLocalISO(date) : "";
};

/* ── Changing a game's date in the Game Library (in-game parity) ─────── */

/** The earliest day a game can be moved to (the API rejects earlier). */
export const EARLIEST_GAME_DAY = "2000-01-01";

/** The latest day a game can be moved to: a year from today, here. */
export const latestGameDay = (now: Date = new Date()): string => {
  const latest = new Date(now);
  latest.setDate(latest.getDate() + 365);
  return todayLocalISO(latest);
};

/**
 * The ISO timestamp to store for a game moved to `day` (`YYYY-MM-DD`): NOON of
 * that day here, so teammates a few time zones away see the same day — a
 * late-evening time would show as the next day further east. Null for
 * anything but a real day from 2000 to a year ahead, which is what the API
 * accepts (half-typed years like 0002 included).
 */
export const gameDateForDay = (day: string, now: Date = new Date()): string | null => {
  const trimmed = day.trim();
  const match = DATE_ONLY.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const noon = new Date(year, month - 1, date, 12);
  // Feb 30 and the like roll into the next month: not a day anyone chose.
  if (noon.getFullYear() !== year || noon.getMonth() !== month - 1 || noon.getDate() !== date) {
    return null;
  }
  if (trimmed < EARLIEST_GAME_DAY || trimmed > latestGameDay(now)) return null;
  return noon.toISOString();
};
